import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@hapana/shared/web/services/gqty'
import { formatTimeAgo } from '@hapana/shared/utils/date.util'
import { useMe } from '../../../hooks/me.hooks'
import { useSupportAssistant } from '../support-assistant.hooks'

interface Chat {
  supportChatId: string
  previewText: string
  updatedAt: string
  status: 'ACTIVE' | 'ENDED'
}

interface ChatHistoryListProps {
  onChatClick: (chatId: string, chatTitle?: string) => void
}

export function ChatHistoryList({ onChatClick }: ChatHistoryListProps) {
  const { t } = useTranslation('core')
  const {} = useSupportAssistant()
  const { data: meData } = useMe() as { data: any }
  const userId = meData?.me?.id

  // Track if we've ever loaded chats successfully
  const [hasLoadedChats, setHasLoadedChats] = useState(false)
  // Track if minimum load time has passed (only on first load)
  const [minLoadTimeComplete, setMinLoadTimeComplete] = useState(false)

  // Query all user chats (up to 100)
  const query = useQuery({ suspense: false }) as any
  const allChatsRaw = userId
    ? (query.userSupportChats({
        userId,
        pagination: { limit: 100 },
      }) as Chat[] | undefined)
    : undefined

  // Explicitly select fields to ensure GQty fetches them
  allChatsRaw?.forEach((chat: any) => {
    chat.supportChatId
    chat.previewText
    chat.updatedAt
    chat.createdAt
    chat.status
  })

  // Filter out chats with undefined/null supportChatId
  const allChats = allChatsRaw?.filter((chat) => chat?.supportChatId)

  // Wait minimum 300ms before showing content (only on first load)
  useEffect(() => {
    if (!hasLoadedChats) {
      const timer = setTimeout(() => {
        setMinLoadTimeComplete(true)
      }, 300)
      return () => clearTimeout(timer)
    } else {
      setMinLoadTimeComplete(true)
    }
  }, [hasLoadedChats])

  // Mark as loaded once we have data (even if empty array)
  useEffect(() => {
    if (allChatsRaw !== undefined && Array.isArray(allChatsRaw)) {
      setHasLoadedChats(true)
    }
  }, [allChatsRaw])

  // Determine loading state
  // Show skeleton ONLY if: we haven't loaded chats before AND (min time hasn't elapsed OR data is undefined)
  const isLoading = !hasLoadedChats && (!minLoadTimeComplete || allChatsRaw === undefined)

  return (
    <Box sx={{ flex: 1, overflowY: 'auto' }}>
      {isLoading ? (
        // Skeleton loader for 10 rows
        <List>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((index) => (
            <ListItem key={`skeleton-${index}`} disablePadding>
              <Box
                sx={{
                  py: 2,
                  px: 3,
                  width: '100%',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="20%" height={20} />
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      ) : allChats && allChats.length > 0 ? (
        <List>
          {allChats.map((chat) => {
            const timeAgo = formatTimeAgo(chat.updatedAt)

            const chatTitle = chat.previewText
              ? chat.previewText.length > 30
                ? `${chat.previewText.substring(0, 30)}...`
                : chat.previewText
              : t('supportAssistant.recentChats.noPreview')

            return (
              <ListItem key={chat.supportChatId} disablePadding data-testid="chat-history-item">
                <ListItemButton
                  onClick={() => onChatClick(chat.supportChatId, chatTitle)}
                  sx={{
                    py: 1.25,
                    px: 2.5,
                    minHeight: 'auto',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                          }}
                        >
                          {chatTitle}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.6875rem',
                            flexShrink: 0,
                            lineHeight: 1,
                          }}
                        >
                          {timeAgo}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      ) : null}
    </Box>
  )
}
